import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { Bar } from "react-chartjs-2";
import html2canvas from "html2canvas";
import * as XLSX from "xlsx";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

function ReportRegion() {
  const { state } = useLocation();

  const graphData = state.regionData.data; // Array of { year, value }
  const reportName = state.regionData.reportName;
  const countryData = state.regionData.region;
  const firstYear = graphData[0].year; // First year
  const lastYear = graphData[graphData.length - 1].year; // Last year
  const cagr = state.regionData.cagr;
  const cagrPercentage = `${(parseFloat(cagr) * 100).toFixed(0)}%`;
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Prepare the data for the bar chart
  const chartData = {
    labels: graphData.map((item) => item.year), // Extract years
    datasets: [
      {
        label: `Revenue (${firstYear}-${lastYear})`,
        data: graphData.map((item) => item.value), // Extract values
        backgroundColor: "rgba(243, 63, 125, 1)", // Bar color
        borderColor: "rgba(75, 192, 192, 1)",
        borderWidth: 1,
        datalabels: {
          display: false, // Disable data labels
        },
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      title: {
        display: true,
        text: `Revenue Trend (${firstYear}-${lastYear})`,
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            return `$${tooltipItem.raw.toFixed(2)}`; // Format values as currency
          },
        },
      },
    },
  };

  // Download chart as PNG
  const downloadChartAsPNG = () => {
    const canvas = document.getElementById("chartCanvas");
    html2canvas(canvas).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = imgData;
      link.download = `${reportName}_chart.png`;
      link.click();
    });
  };

  // Download data as Excel
  const downloadDataAsExcel = () => {
    const data = [
      ["Year", "Revenue"], // Header row
      ...graphData.map((item) => [item.year, item.value]), // Data rows
    ];

    const ws = XLSX.utils.aoa_to_sheet(data); // Create a worksheet
    const wb = XLSX.utils.book_new(); // Create a workbook
    XLSX.utils.book_append_sheet(wb, ws, "Revenue Data"); // Append sheet to workbook

    XLSX.writeFile(wb, `${reportName}_data.xlsx`); // Export to Excel file
  };

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-800 mb-4">{reportName}</h1>
      <h2 className="text-xl font-semibold text-gray-800 mb-4">
        Country Report for {countryData}
      </h2>

      {/* Info grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded shadow p-4 text-center">
          <h3 className="text-gray-800 font-semibold">Start Year</h3>
          <p className="text-xl font-bold text-green-600">{firstYear}</p>
        </div>
        <div className="bg-white rounded shadow p-4 text-center">
          <h3 className="text-gray-800 font-semibold">End Year</h3>
          <p className="text-xl font-bold text-blue-600">{lastYear}</p>
        </div>
        <div className="bg-white rounded shadow p-4 text-center">
          <h3 className="text-gray-800 font-semibold">
            CAGR ({firstYear}-{lastYear})
          </h3>
          <p className="text-xl font-bold text-purple-600">{cagrPercentage}</p>
        </div>
        <div className="bg-white rounded shadow p-4 text-center">
          <h3 className="text-gray-800 font-semibold">Region</h3>
          <p className="text-xl font-bold text-yellow-600">All</p>
        </div>
      </div>
      <div className="download-options text-right mt-6">
        <div className="relative inline-block text-left">
          <button
            className="bg-black text-white px-4 py-2 rounded inline-flex items-center"
            onClick={() => setDropdownOpen(!dropdownOpen)}
          >
            Download Options
            <svg
              className="ml-2 w-4 h-4"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 11.293l3.71-4.063a.75.75 0 111.08 1.04l-4.5 4.916a.75.75 0 01-1.08 0l-4.5-4.916a.75.75 0 01.02-1.06z"
                clipRule="evenodd"
              />
            </svg>
          </button>

          {dropdownOpen && (
            <div
              className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10"
              onMouseLeave={() => setDropdownOpen(false)}
            >
              <div className="py-1">
                <button
                  onClick={downloadChartAsPNG}
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                >
                  Download PNG
                </button>
                <button
                  onClick={downloadDataAsExcel}
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                >
                  Download Excel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Bar Chart */}
      <div
        className="chart-container"
        style={{ width: "80%", height: "400px", margin: "auto" }}
      >
        <Bar id="chartCanvas" data={chartData} options={chartOptions} />
      </div>

      {/* Download options below the graph */}
      {/* <div className="download-options text-center mt-6">
        <button
          onClick={downloadChartAsPNG}
          className="bg-blue-600 text-white px-4 py-2 rounded mr-2"
        >
          Download PNG
        </button>
        <button
          onClick={downloadDataAsExcel}
          className="bg-yellow-600 text-white px-4 py-2 rounded"
        >
          Download Excel
        </button>
      </div> */}
    </div>
  );
}

export default ReportRegion;
